import { useTheme } from "@/theme/ThemeProvider";
import { Platform, Pressable, Text, View, ViewStyle } from "react-native";

export type OptionChip<T extends string = string> = {
  value: T;
  label: string;
  disabled?: boolean;
  hint?: string;
  /** Optional border/text accent (e.g. policy severity). */
  accentColor?: string;
};

type Props<T extends string> = {
  options: OptionChip<T>[];
  value: T | null;
  onChange: (value: T | null) => void;
  /** If true, tapping selected chip deselects it. Default true. */
  allowDeselect?: boolean;
  style?: ViewStyle;
  size?: "sm" | "md";
  /**
   * Mindest-Touch-Höhe. Die Chips umbrechen in der Zeile,
   * der Text darf bei großer Schrift auf mehrere Zeilen wachsen.
   */
  hitTargetMin?: number;
  /**
   * `radio`: role=radio, accessibilityState.checked und auf Web aria-checked.
   * Standard bleibt der Button, damit bestehende Chip-Gruppen unverändert sind.
   */
  selection?: "button" | "radio";
};

export function OptionChipGroup<T extends string>({
  options,
  value,
  onChange,
  allowDeselect = true,
  style,
  size = "md",
  hitTargetMin,
  selection = "button",
}: Props<T>) {
  const { theme } = useTheme();
  const padV = size === "sm" ? theme.space.xs : theme.space.xs;
  const padH = size === "sm" ? theme.space.sm : theme.space.sm;
  const fontSize = size === "sm" ? 12 : 13;
  const roomy = hitTargetMin != null;
  const radio = selection === "radio";

  return (
    <View
      style={[
        { flexDirection: "row", flexWrap: "wrap", gap: theme.space.xs },
        roomy
          ? { width: "100%", maxWidth: "100%", minWidth: 0, alignItems: "flex-start" }
          : null,
        style,
      ]}
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        const borderColor = selected
          ? opt.accentColor ?? theme.color.primary
          : opt.accentColor ?? theme.color.border;
        return (
          <Pressable
            key={opt.value}
            disabled={opt.disabled}
            onPress={() => {
              if (opt.disabled) return;
              if (selected && allowDeselect) onChange(null);
              else onChange(opt.value);
            }}
            accessibilityRole={radio ? "radio" : roomy ? "button" : undefined}
            accessibilityLabel={radio || roomy ? opt.label : undefined}
            accessibilityState={
              radio
                ? { checked: selected, disabled: Boolean(opt.disabled) }
                : roomy
                  ? { selected, disabled: Boolean(opt.disabled) }
                  : undefined
            }
            {...(radio && Platform.OS === "web" ? { "aria-checked": selected } : {})}
            style={{
              maxWidth: roomy ? "100%" : undefined,
              minHeight: roomy ? hitTargetMin : undefined,
              flexShrink: roomy ? 0 : undefined,
              justifyContent: roomy ? "center" : undefined,
              paddingVertical: padV,
              paddingHorizontal: padH,
              borderRadius: theme.radius.md,
              borderWidth: selected ? 2 : 1,
              borderColor,
              backgroundColor: selected ? theme.color.accentBg : theme.color.surface,
              opacity: opt.disabled ? 0.45 : 1,
            }}
          >
            <Text
              style={{
                fontSize,
                fontWeight: "600",
                flexShrink: roomy ? 1 : undefined,
                color: selected ? opt.accentColor ?? theme.color.primary : theme.color.text,
              }}
            >
              {opt.label}
            </Text>
            {opt.hint ? (
              <Text style={{ fontSize: 10, color: theme.color.muted, marginTop: 2 }}>{opt.hint}</Text>
            ) : null}
          </Pressable>
        );
      })}
    </View>
  );
}
